import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { LogOut, User, ShieldCheck, Package } from 'lucide-react';
import { toast } from 'sonner';

export default function AccountPage() {
  const { user, loading, login, register, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLogin, setIsLogin] = useState(true);
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (searchParams.get('mode') === 'register') setIsLogin(false);
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    let result;
    if (isLogin) {
      result = await login(form.email, form.password);
    } else {
      if (!form.name) {
        setError('Inserisci il tuo nome');
        setSubmitting(false);
        return;
      }
      result = await register(form.name, form.email, form.password);
    }

    if (result.success) {
      toast.success(isLogin ? 'Benvenuto!' : 'Registrazione completata!');
      // Respect ?next= (e.g. customer logging from shop or product detail)
      const next = searchParams.get('next');
      const fromShop = searchParams.get('from');
      if (next && next.startsWith('/')) {
        navigate(next);
      } else if (fromShop) {
        navigate(`/negozio/${fromShop}`);
      } else if (result.data?.role === 'superadmin') navigate('/superadmin');
      else if (result.data?.role === 'admin') navigate('/admin');
    } else {
      setError(result.error);
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="pb-safe flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-2 border-[#C25934] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (user) {
    return (
      <div data-testid="account-page" className="pb-safe px-4 pt-8">
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-[#C25934]/10 rounded-full flex items-center justify-center mx-auto mb-3">
            {user.role === 'superadmin' ? <ShieldCheck size={36} className="text-[#C25934]" /> : isAdmin ? <ShieldCheck size={36} className="text-[#C25934]" /> : <User size={36} className="text-[#C25934]" />}
          </div>
          <h2 className="text-2xl font-semibold text-[#2A2624]" style={{ fontFamily: 'Outfit' }}>
            {user.name || 'Utente'}
          </h2>
          <p className="text-[#877E78] text-sm">{user.email}</p>
          <span className="inline-block mt-2 text-[10px] uppercase tracking-[0.2em] font-bold text-[#C25934] bg-[#C25934]/10 px-3 py-1 rounded-full">
            {user.role === 'superadmin' ? 'Super Amministratore' : user.role === 'admin' ? 'Negoziante' : 'Cliente'}
          </span>
        </div>

        <div className="space-y-3">
          {user.role === 'superadmin' && (
            <Button
              data-testid="goto-superadmin-btn"
              onClick={() => navigate('/superadmin')}
              className="w-full h-12 rounded-xl bg-[#2A2624] hover:bg-[#1a1917] text-white"
            >
              <ShieldCheck size={18} className="mr-2" /> Pannello SuperAdmin
            </Button>
          )}

          {isAdmin && (
            <>
              <Button
                data-testid="goto-admin-btn"
                onClick={() => navigate('/admin')}
                className="w-full h-12 rounded-xl bg-[#C25934] hover:bg-[#A64B2B] text-white"
              >
                <Package size={18} className="mr-2" /> Gestione Prodotti
              </Button>
              <Button
                data-testid="goto-orders-btn"
                onClick={() => navigate('/ordini')}
                variant="outline"
                className="w-full h-12 rounded-xl border-[#E8E3D9] text-[#5C554F]"
              >
                <Package size={18} className="mr-2" /> Gestione Ordini
              </Button>
              <Button
                data-testid="goto-settings-btn"
                onClick={() => navigate('/negozio/impostazioni')}
                variant="outline"
                className="w-full h-12 rounded-xl border-[#E8E3D9] text-[#5C554F]"
              >
                <Package size={18} className="mr-2" /> Impostazioni Negozio
              </Button>
            </>
          )}

          <Button
            data-testid="logout-btn"
            onClick={async () => { await logout(); toast.success('Logout effettuato'); }}
            variant="outline"
            className="w-full h-12 rounded-xl border-[#E8E3D9] text-[#5C554F]"
          >
            <LogOut size={18} className="mr-2" /> Esci
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="login-page" className="pb-safe px-4 pt-8 max-w-md mx-auto">
      {searchParams.get('from') && (
        <button
          data-testid="account-back-to-shop"
          onClick={() => navigate(`/negozio/${searchParams.get('from')}`)}
          className="inline-flex items-center gap-1.5 text-xs text-[#5C554F] hover:text-[#C25934] mb-3"
        >
          ← Torna al negozio
        </button>
      )}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-semibold text-[#2A2624] tracking-tight" style={{ fontFamily: 'Outfit' }}>
          {searchParams.get('from') ? 'Accedi' : 'PhotoVendo'}
        </h1>
        <p className="text-[#877E78] text-sm mt-1">
          {isLogin ? 'Accedi al tuo account' : 'Crea un nuovo account'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {!isLogin && (
          <div>
            <label className="text-sm font-medium text-[#5C554F]">Nome</label>
            <Input
              data-testid="auth-name"
              placeholder="Il tuo nome"
              value={form.name}
              onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
              className="h-12 rounded-xl border-[#E8E3D9] mt-1"
            />
          </div>
        )}
        <div>
          <label className="text-sm font-medium text-[#5C554F]">Email</label>
          <Input
            data-testid="auth-email"
            type="email"
            placeholder="email@esempio.com"
            value={form.email}
            onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
            className="h-12 rounded-xl border-[#E8E3D9] mt-1"
            required
          />
        </div>
        <div>
          <label className="text-sm font-medium text-[#5C554F]">Password</label>
          <Input
            data-testid="auth-password"
            type="password"
            placeholder="La tua password"
            value={form.password}
            onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
            className="h-12 rounded-xl border-[#E8E3D9] mt-1"
            required
          />
        </div>

        {error && (
          <div data-testid="auth-error" className="text-sm text-red-500 bg-red-50 rounded-xl p-3">
            {error}
          </div>
        )}

        <Button
          data-testid="auth-submit-btn"
          type="submit"
          disabled={submitting}
          className="w-full h-14 rounded-full bg-[#C25934] hover:bg-[#A64B2B] text-white text-base font-semibold"
          style={{ fontFamily: 'Outfit' }}
        >
          {submitting ? 'Attendere...' : (isLogin ? 'Accedi' : 'Registrati')}
        </Button>
      </form>

      {isLogin && (
        <p className="text-center text-xs mt-3">
          <button
            data-testid="forgot-password-btn"
            type="button"
            onClick={async () => {
              const email = form.email || window.prompt('Inserisci la tua email per ricevere il link di reset:');
              if (!email || !/.+@.+\..+/.test(email)) { toast.error('Email non valida'); return; }
              try {
                await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/forgot-password`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ email }),
                });
                toast.success('Se l\'email è registrata, riceverai un link entro pochi minuti.');
              } catch {
                toast.error('Errore di rete, riprova');
              }
            }}
            className="text-[#877E78] hover:text-[#C25934] underline"
          >
            Password dimenticata?
          </button>
        </p>
      )}

      <p className="text-center text-sm text-[#877E78] mt-4">
        {isLogin ? 'Non hai un account? ' : 'Hai già un account? '}
        <button
          data-testid="toggle-auth-mode"
          onClick={() => { setIsLogin(!isLogin); setError(''); }}
          className="text-[#C25934] font-medium hover:underline"
        >
          {isLogin ? 'Registrati' : 'Accedi'}
        </button>
      </p>
    </div>
  );
}
